import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import {
  useApi,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import GitHubIcon from '@material-ui/icons/GitHub';
import DescriptionIcon from '@material-ui/icons/Description';
import BugReportIcon from '@material-ui/icons/BugReport';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import SettingsIcon from '@material-ui/icons/Settings';
import LockIcon from '@material-ui/icons/Lock';
import LinkIcon from '@material-ui/icons/Link';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import LanguageIcon from '@material-ui/icons/Language';

import { ENV_DASHBOARD_ANNOTATION } from '../coalitions';
import {
  AppEnvironment,
  ENV_LABEL,
  entityEnvironments,
  envLinks,
} from './appEnvironments';
import { fetchAppEndpoints, hasAppSelector } from './appEndpoints';

// Replaces the default EntityLinksCard on the Overview page.
//
// Merges four link sources, in order:
//   0. APP ENDPOINTS — the app's live URL per environment (Ingress URLs from
//      ArgoCD, see appEndpoints.ts); only for entities with argocd/app-selector.
//   1. CONVENTION-DERIVED GITHUB — from `github.com/project-slug` annotation,
//      generates Repository / README / Issues / PRs.
//   2. CONVENTION-DERIVED OPS — from `sparrow.io/bu` (or inferred) + entity
//      name, generates Config (folder in the BU's config repo) and
//      Secrets (Vault UI URL) for each environment the app runs in
//      (`sparrow.io/environments`). Only rendered when we know how to
//      build the URL for that BU.
//   3. ENTITY-DECLARED — anything in `metadata.links[]`. Rendered last.
//
// Card doesn't render if all three sources are empty.
interface LinkEntry {
  url: string;
  title: string;
  secondary?: string;
  icon: React.ReactNode;
}

const iconFor = (name?: string): React.ReactNode => {
  switch (name) {
    case 'github':
      return <GitHubIcon />;
    case 'docs':
      return <DescriptionIcon />;
    case 'issues':
      return <BugReportIcon />;
    case 'merge':
      return <MergeTypeIcon />;
    case 'config':
      return <SettingsIcon />;
    case 'secret':
      return <LockIcon />;
    default:
      return <LinkIcon />;
  }
};

// Mirrors the BU inference in EntityLogsIframe / EntityObservabilityCard.
const inferBuFromGithubOrg = (org: string | undefined): string | undefined => {
  if (!org) return undefined;
  if (org === 'sparrow-platforms') return 'platforms';
  return org;
};

export const EntityConventionLinksCard = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const annotations = entity.metadata.annotations ?? {};
  const slug = annotations['github.com/project-slug'];

  // BU resolution — same priority as other components:
  // annotation > entity namespace > github org name inference.
  const orgFromSlug = slug?.split('/')[0];
  const bu =
    annotations['sparrow.io/bu'] ??
    (entity.metadata.namespace && entity.metadata.namespace !== 'default'
      ? entity.metadata.namespace
      : undefined) ??
    inferBuFromGithubOrg(orgFromSlug);

  // SparrowDesk entities render Repository via EntitySparrowDeskLinksCard
  // (which handles both BE + FE per app). Suppressing the generic github
  // section here avoids showing "Repository" twice on the Overview.
  // Any entity opted into a coalition dashboard (sparrowdesk-application,
  // sparrowdesk-website, etc.) gets its Repository / Config / Vault
  // links from the coalition components, so suppress the generic github
  // section here to avoid duplication.
  const isEnvDashboardEntity = Boolean(annotations[ENV_DASHBOARD_ANNOTATION]);

  // Issues is intentionally omitted — SurveySparrow tracks work in Jira,
  // not GitHub Issues. Add a service-specific Jira link via
  // metadata.links in the entity's catalog-info.yaml if a team wants
  // a per-service board link on the Overview.
  const githubLinks: LinkEntry[] = slug && !isEnvDashboardEntity
    ? [
        {
          url: `https://github.com/${slug}`,
          title: 'Repository',
          icon: iconFor('github'),
        },
        {
          url: `https://github.com/${slug}#readme`,
          title: 'README',
          icon: iconFor('docs'),
        },
        {
          url: `https://github.com/${slug}/pulls`,
          title: 'Pull requests',
          icon: iconFor('merge'),
        },
      ]
    : [];

  // ---------- Ops links (Config + Secrets), one pair per environment ----------
  //
  // URLs come from appEnvironments.envLinks (per-BU, per-env frontend
  // config under `sparrow.<bu>.<staging|production>`). Environments come
  // from `sparrow.io/environments`; entities without it are staging-only.
  // Coalition dashboards own their Config + Vault links, so skip them here.
  // An entity with no config folder or Vault secret (a library, a test suite)
  // opts out with `sparrow.io/hide: config,secrets`.
  const hidden = (annotations['sparrow.io/hide'] ?? '')
    .split(',')
    .map(p => p.trim().toLowerCase());
  const opsLinks: LinkEntry[] = [];
  if (bu && !isEnvDashboardEntity) {
    const envs = entityEnvironments(entity);
    const suffix = (env: AppEnvironment) =>
      envs.length > 1 ? ` · ${ENV_LABEL[env]}` : '';
    for (const env of envs) {
      const found = envLinks(config, entity, env);
      const cfg = hidden.includes('config') ? undefined : found.config;
      const secrets = hidden.includes('secrets') ? undefined : found.secrets;
      if (cfg) {
        opsLinks.push({
          url: cfg.url,
          title: `Config${suffix(env)}`,
          secondary: cfg.path,
          icon: iconFor('config'),
        });
      }
      if (secrets) {
        opsLinks.push({
          url: secrets.url,
          title: `Secrets${suffix(env)}`,
          secondary: secrets.path,
          icon: iconFor('secret'),
        });
      }
    }
  }

  const entityLinks: LinkEntry[] = (entity.metadata.links ?? []).map(l => ({
    url: l.url,
    title: l.title ?? l.url,
    icon: iconFor(l.icon),
  }));

  // Live app endpoints per environment (Ingress URLs reported by ArgoCD),
  // listed first: what most people open the page for.
  const { value: endpoints = [] } = useAsync(
    async () => (hasAppSelector(entity) ? fetchAppEndpoints(entity, discovery, fetchApi) : []),
    [entity, discovery, fetchApi],
  );
  const endpointLinks: LinkEntry[] = endpoints.map(e => ({
    url: e.url,
    title: `App · ${ENV_LABEL[e.env]}`,
    secondary: e.url.replace(/^https?:\/\//, ''),
    icon: <LanguageIcon />,
  }));

  const links = [...endpointLinks, ...githubLinks, ...opsLinks, ...entityLinks];
  if (links.length === 0) return null;

  return (
    <InfoCard title="Links">
      <List dense>
        {links.map(link => (
          <ListItem
            key={`${link.title}::${link.url}`}
            button
            component="a"
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ListItemIcon>{link.icon}</ListItemIcon>
            <ListItemText primary={link.title} secondary={link.secondary} />
            <OpenInNewIcon fontSize="small" color="action" />
          </ListItem>
        ))}
      </List>
    </InfoCard>
  );
};

export default EntityConventionLinksCard;
